import React from "react";
import get from "lodash-es/get";
import { Input } from "@components/primitives/input";
import { useUserProfile } from "@providers/profile";
import { passwordSchema } from "@utils/validations";
import { Form, Formik, FormikProps } from "formik";
import { AccountFormState } from "./AccountForm.model";
import { formatErrorMessage } from "@utils/formatErrorMessage";
import { events, identify } from "@providers/analytics/analytics";
import { CancelButton } from "@components/account/Account.styled";

const ChangePasswordForm = ({ state, dispatchState }: AccountFormState) => {
  const {
    profileState: { profile, authToken, authState },
    changePassword,
  } = useUserProfile();

  const onSubmitPassword = async (values: any) => {
    const { currentPassword, password, passwordConfirmation } = values;

    // loading set to true before making the request
    dispatchState({ ...state, loading: true });

    // Abort if new password is the same as current password.
    if (currentPassword === password && currentPassword === passwordConfirmation) {
      dispatchState({ ...state, error: "New password must be different from current password." });
      return;
    }

    try {
      await changePassword(password, passwordConfirmation, currentPassword, authToken);
      // triggers a re-render of the component
      dispatchState({
        ...state,
        updated: "Password updated.",
        editing: "default",
      });
      // identify & events are from segment
      identify(authState.value);
      events.account.updated(authState.value);
    } catch (e) {
      dispatchState({ ...state, error: formatErrorMessage(e) });
    }
  };

  const onCancel = () => {
    dispatchState({ ...state, editing: "default", error: null });
  };

  return (
    <Formik
      onSubmit={(values, { setSubmitting }) => {
        onSubmitPassword(values).then(() => setSubmitting(false));
      }}
      validationSchema={passwordSchema}
      initialValues={{ currentPassword: "", password: "", passwordConfirmation: "" }}>
      {(
        props: FormikProps<{
          currentPassword: string;
          password: string;
          passwordConfirmation: string;
        }>
      ) => {
        const { values, errors, touched, handleChange, handleBlur, isSubmitting } = props;
        return (
          <Form className="grid-x grid-margin-x">
            <div className="cell small-12 ">
              {state.error && <p className="error">{state.error}</p>}
              <dl>
                <dt>Email Address</dt>
                <dd>{profile?.email}</dd>
              </dl>
            </div>
            <div className="cell medium-12 large-6">
              <Input
                type="password"
                name="currentPassword"
                required
                label="Current Password"
                autoComplete="current-password"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.currentPassword}
                error={errors.currentPassword && touched.currentPassword && errors.currentPassword}
              />
              <Input
                type="password"
                name="password"
                required
                label="New Password"
                autoComplete="new-password"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.password}
                error={errors.password && touched.password && errors.password}
              />
              <Input
                type="password"
                name="passwordConfirmation"
                required
                label="Confirm New Password"
                autoComplete="new-password"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.passwordConfirmation}
                error={
                  errors.passwordConfirmation &&
                  touched.passwordConfirmation &&
                  errors.passwordConfirmation
                }
              />
              <div>
                <input
                  type="submit"
                  value="Change Password"
                  className="button inverted"
                  disabled={isSubmitting}
                />
                <CancelButton onClick={onCancel}>or cancel</CancelButton>
              </div>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ChangePasswordForm;
